// BUILD-046 Owner direction of 2026-10-21 (cycle A, correction A): the page's ONE signal for "what is today in Phoenix".
//
// `useCurrentPhoenixDay(onDayChange)` is used once, by MyWorkPage. It returns the current Phoenix day (YYYY-MM-DD) as React state and keeps it right while the page stays
// open: it moves at 00:00 Phoenix and when the tab becomes visible again after the day moved (phoenixDayWatcher.ts). When the day moves it sets the state (so every row, the
// filters, the counts and Recommended are drawn with the new day at once) and calls `onDayChange`, which MyWorkPage binds to the silent re-read of the loaded lists.
//
// `PhoenixDayContext` carries that day down to the rows (MineRow, the Shared and Team rows, the Due cell of TaskTable, the legacy Team Work list), and `usePhoenixDay()`
// reads it. Outside the page (a row drawn on its own) it falls back to today in Phoenix at the time of the render, which is what those rows did before.
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { phoenixToday } from './dateLabels';
import { createDayWatcher, createDomVisibilitySource } from './phoenixDayWatcher';

export const PhoenixDayContext = createContext<string | null>(null);

/** The Phoenix day the rows classify dates by: the page's signal, else today in Phoenix read now. */
export function usePhoenixDay(): string {
  const day = useContext(PhoenixDayContext);
  return day ?? phoenixToday();
}

/** The current Phoenix day as state; `onDayChange(newDay, previousDay)` runs when it moves (always the latest closure the caller passed). */
export function useCurrentPhoenixDay(onDayChange: (newDay: string, previousDay: string) => void): string {
  const [day, setDay] = useState<string>(() => phoenixToday());
  const latest = useRef(onDayChange);
  latest.current = onDayChange;
  useEffect(() => {
    const watcher = createDayWatcher({
      now: () => new Date(),
      setTimeout: (callback, ms) => window.setTimeout(callback, ms),
      clearTimeout: (handle) => window.clearTimeout(handle as number),
      visibilitySource: createDomVisibilitySource(document, window),
      onDayChange: (newDay, previousDay) => {
        setDay(newDay);
        latest.current(newDay, previousDay);
      },
    });
    // The day may already have moved between the first render and this effect; the state follows it (the lists were read after the render, so no re-read is owed).
    setDay(watcher.start());
    return () => watcher.stop();
  }, []);
  return day;
}
